import type { Session, User } from '@supabase/supabase-js';

const STORAGE_KEY = 'immokompass_local_mock_auth_email';

/** AppShell bleibt nach Demo-Login gemountet — gleicher Tab feuert kein neues Supabase-Auth-Event; über dieses Event Session neu einlesen. */
export const LOCAL_MOCK_AUTH_CHANGED_EVENT = 'immokompass-local-mock-auth-changed';

/** Nur Loopback — Mock/Testdaten nicht über LAN-IP (192.168 …) anzeigen. */
export function isLocalhostOnly(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
}

/**
 * Auf localhost ist Demo-Anmeldung standardmäßig an (kein Request zu Supabase).
 * Setze `VITE_MOCK_AUTH=false` in der .env, um gegen die echte Instanz zu testen.
 */
export function isMockAuthExplicitlyDisabled(): boolean {
  const v = import.meta.env.VITE_MOCK_AUTH;
  return v === 'false' || v === '0';
}

export function isMockAuthActive(): boolean {
  return isLocalhostOnly() && !isMockAuthExplicitlyDisabled();
}

export function getMockLoginEmail(): string {
  return import.meta.env.VITE_MOCK_LOGIN_EMAIL?.trim() || 'demo@immokompass.local';
}

export function getMockLoginPassword(): string {
  return import.meta.env.VITE_MOCK_LOGIN_PASSWORD?.trim() || 'demo123456';
}

export function getStoredMockEmail(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  const v = sessionStorage.getItem(STORAGE_KEY);
  return v?.trim() ? v.trim() : null;
}

export function establishMockSession(email: string): void {
  sessionStorage.setItem(STORAGE_KEY, email.trim());
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(LOCAL_MOCK_AUTH_CHANGED_EVENT));
  }
}

export function clearMockSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(LOCAL_MOCK_AUTH_CHANGED_EVENT));
  }
}

/** Sidebar / Kennzeichnung: Demo-Session aktiv (localhost, nicht VITE_MOCK_AUTH=false). */
export function isLocalDemoSession(): boolean {
  return Boolean(isMockAuthActive() && getStoredMockEmail());
}

export function buildMockUser(email: string): User {
  const now = new Date().toISOString();
  return {
    id: '00000000-0000-4000-8000-000000000001',
    aud: 'authenticated',
    role: 'authenticated',
    email: email.trim(),
    email_confirmed_at: now,
    phone: '',
    confirmed_at: now,
    last_sign_in_at: now,
    app_metadata: {},
    user_metadata: {
      given_name: 'Local',
      family_name: 'Dev',
      full_name: 'Local Dev',
    },
    identities: [],
    created_at: now,
    updated_at: now,
    is_anonymous: false,
  };
}

export function buildMockSession(email: string): Session {
  const user = buildMockUser(email);
  const exp = Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 365;
  return {
    access_token: 'mock-local-dev-token',
    token_type: 'bearer',
    expires_in: 60 * 60 * 24 * 365,
    expires_at: exp,
    refresh_token: 'mock-local-dev-refresh',
    user,
  } satisfies Session;
}
